import type { InvoiceBankAccount, ReceivableDraft, Seller } from "./types";

/**
 * Which connected account an invoice is paid into (FIN-283), shared by both apps' forms so
 * the field, the Payment method field, the preview and the send gate all agree. See
 * specs/receiving-bank-account/requirements.md.
 */
export type ReceivingAccountSelection =
  /** No connected account: the manual-account fallback applies, and there is nothing to pick. */
  | { kind: "none" }
  /** Exactly one active account: it is the account, shown read-only (EC1). */
  | { kind: "single"; account: InvoiceBankAccount }
  /** The sender's choice, or the default (the 1930 account) when they made none. */
  | { kind: "chosen"; account: InvoiceBankAccount; isDefault: boolean; accounts: InvoiceBankAccount[] }
  /** Several accounts and none on 1930: the sender must pick (EC3). */
  | { kind: "choice_required"; accounts: InvoiceBankAccount[] }
  /** The draft names an account that is no longer connected (EC4). */
  | { kind: "unavailable"; accounts: InvoiceBankAccount[] };

export const RECEIVING_ACCOUNT_REQUIRED = "Choose which bank account the customer pays into";
export const RECEIVING_ACCOUNT_UNAVAILABLE =
  "The chosen bank account is no longer connected — choose which account the customer pays into";

/** What follows the account's name in the notice shown when a pick drops the bankgiro (EC5). */
export const NO_BANKGIRO_NOTICE_SUFFIX = " has no bankgiro, so this invoice is paid to the bank account instead.";
export const CARRY_OVER_DEFAULT_NOTICE =
  "The original invoice's bank account is no longer connected, so this copy uses your default account.";
export const CARRY_OVER_CHOOSE_NOTICE =
  "The original invoice's bank account is no longer connected — choose which account this copy is paid into.";

export function selectReceivingAccount(seller: Seller | null, chosenId: string | null): ReceivingAccountSelection {
  const accounts = seller?.invoiceBankAccounts ?? [];
  if (accounts.length === 0) return { kind: "none" };
  if (chosenId) {
    const chosen = accounts.find((a) => a.id === chosenId);
    if (!chosen) return { kind: "unavailable", accounts };
    if (accounts.length === 1) return { kind: "single", account: chosen };
    return { kind: "chosen", account: chosen, isDefault: chosen.receivesInvoices, accounts };
  }
  if (accounts.length === 1) return { kind: "single", account: accounts[0] };
  const defaultAccount = accounts.find((a) => a.receivesInvoices);
  return defaultAccount
    ? { kind: "chosen", account: defaultAccount, isDefault: true, accounts }
    : { kind: "choice_required", accounts };
}

export function receivingAccountOf(selection: ReceivingAccountSelection): InvoiceBankAccount | null {
  return selection.kind === "single" || selection.kind === "chosen" ? selection.account : null;
}

/**
 * The seller as an invoice paid into `chosenId` presents it: that account's bank details, its
 * bankgiro (and only its), and it marked as the receiving account. Everything downstream (the
 * Payment method field, the preview, the send gate) reads this instead of the company default.
 * An unusable choice clears the payment details rather than borrowing another account's.
 */
export function sellerPaidInto(seller: Seller | null, chosenId: string | null): Seller | null {
  if (!seller) return null;
  const selection = selectReceivingAccount(seller, chosenId);
  if (selection.kind === "none") return seller;
  const account = receivingAccountOf(selection);
  return {
    ...seller,
    paymentReferenceLines: account?.paymentReferenceLines ?? [],
    // The account's own bankgiro, else the company's account-less one (FIN-1172).
    bankgiroAvailable: !!(account?.bankgiro ?? seller.companyBankgiro),
    resolvedPaymentMethod: (account?.bankgiro ?? seller.companyBankgiro) ? "bankgiro" : "bank_account",
    receivingAccountChoiceRequired: !account,
    invoiceBankAccounts: seller.invoiceBankAccounts.map((a) => ({ ...a, receivesInvoices: a.id === account?.id })),
  };
}

/** The manual bank-account form is only for a company with no connected account at all. */
export function needsManualBankAccount(seller: Seller | null): boolean {
  return !!seller && seller.invoiceBankAccounts.length === 0 && seller.paymentReferenceLines.length === 0;
}

export function accountLabel(account: InvoiceBankAccount): string {
  return `${account.bankName} ${account.maskedAccountNumber}`.trim();
}

/**
 * The draft patch for picking another account, and the notice to show when the pick turns a
 * bankgiro invoice into a bank-account one because the new account has no bankgiro (EC5).
 * An explicit Bankgiro choice is changed to Bank account; an automatic one stays automatic.
 */
export function changeReceivingAccount(
  seller: Seller | null,
  draft: Pick<ReceivableDraft, "receivingBankAccountId" | "paymentMethod">,
  nextId: string,
): { patch: Pick<ReceivableDraft, "receivingBankAccountId" | "paymentMethod">; notice: string | null } {
  const before = sellerPaidInto(seller, draft.receivingBankAccountId);
  const after = sellerPaidInto(seller, nextId);
  const shownBefore = draft.paymentMethod ?? before?.resolvedPaymentMethod ?? "bank_account";
  const next = receivingAccountOf(selectReceivingAccount(seller, nextId));
  const loseBankgiro = shownBefore === "bankgiro" && !!next && !after?.bankgiroAvailable;
  return {
    patch: {
      receivingBankAccountId: nextId,
      paymentMethod: loseBankgiro && draft.paymentMethod === "bankgiro" ? "bank_account" : draft.paymentMethod,
    },
    notice: loseBankgiro
      ? `${accountLabel(next!)}${NO_BANKGIRO_NOTICE_SUFFIX}`
      : null,
  };
}

/**
 * A copied invoice keeps its account only while that account is still connected (EC8); the
 * notice says so when it had to fall back to the default.
 */
export function carryOverReceivingAccount(
  seller: Seller | null,
  sourceAccountId: string | null,
): { receivingBankAccountId: string | null; notice: string | null } {
  if (!sourceAccountId) return { receivingBankAccountId: null, notice: null };
  if (selectReceivingAccount(seller, sourceAccountId).kind !== "unavailable") {
    return { receivingBankAccountId: sourceAccountId, notice: null };
  }
  const hasDefault = selectReceivingAccount(seller, null).kind !== "choice_required";
  return {
    receivingBankAccountId: null,
    notice: hasDefault ? CARRY_OVER_DEFAULT_NOTICE : CARRY_OVER_CHOOSE_NOTICE,
  };
}


export interface ReceivingAccountOption {
  id: string;
  label: string;
  currency: string;
  /** "Swedbank ••••5138 · SEK" — what the option reads as. */
  text: string;
  /** Only ever set among several accounts, where the default can only be the 1930 account. */
  isPrimary: boolean;
}

/** Everything the consultant app's "Paid into" field renders (the client portal uses customerPaysTo.ts, FIN-1156). */
export type ReceivingAccountFieldView =
  | { kind: "hidden" }
  | { kind: "readonly"; option: ReceivingAccountOption }
  | { kind: "select"; options: ReceivingAccountOption[]; selectedId: string | undefined; hint: string | null; alert: string | null };

export const RECEIVING_ACCOUNT_CHOICE_HINT =
  "Your primary account (1930) isn't connected, so choose which account customers pay into.";
export const RECEIVING_ACCOUNT_UNAVAILABLE_ALERT =
  "The chosen bank account is no longer connected. Choose which account the customer pays into.";

function toOption(account: InvoiceBankAccount, showPrimary: boolean): ReceivingAccountOption {
  const label = accountLabel(account);
  return {
    id: account.id, label, currency: account.currency,
    text: account.currency ? `${label} · ${account.currency}` : label,
    isPrimary: showPrimary && account.receivesInvoices,
  };
}

export function receivingAccountFieldView(seller: Seller | null, chosenId: string | null): ReceivingAccountFieldView {
  const selection = selectReceivingAccount(seller, chosenId);
  switch (selection.kind) {
    case "none":
      return { kind: "hidden" };
    case "single":
      // A lone account is the default whether or not it is on 1930, so it is not labelled Primary.
      return { kind: "readonly", option: toOption(selection.account, false) };
    default:
      return {
        kind: "select",
        options: selection.accounts.map((a) => toOption(a, true)),
        selectedId: selection.kind === "chosen" ? selection.account.id : undefined,
        hint: selection.kind === "choice_required" ? RECEIVING_ACCOUNT_CHOICE_HINT : null,
        alert: selection.kind === "unavailable" ? RECEIVING_ACCOUNT_UNAVAILABLE_ALERT : null,
      };
  }
}
